/**
 * Parser del PDF de estado de cuenta de Konfio TDC (mensual oficial).
 *
 * El CSV de Konfio omite algunos campos (notablemente movimientos en
 * divisa extranjera y el desglose por titular vs. adicional). El PDF
 * mensual cubre todo. Permitimos subir ambos y deduplicamos en backend.
 *
 * Formato observado:
 *   Periodo del 17 mar 2026 al 17 abr 2026
 *   Historial de movimientos del titular
 *   19 mar 2026   PAGO VIA SPEI   $300.00
 *   29 mar 2026   IKEA HER 170522M19 - 31 mar 2026   $20,908.00
 *   30 mar 2026   CLAUDE.AI SUBSCRIPTION T.C. 18.6087* ...   $3,721.74   $200.00 USD
 *   Subtotal   $ 20,908.00   $ 43,100.00
 *   Historial de movimientos de SERVANDO RAMIREZ - **** 7736
 *   ... (más filas)
 *
 * Reglas:
 *   - "PAGO VIA SPEI" → abono (pago a TDC desde otra cuenta propia).
 *     Marca transfer_in para reconciliación con el cargo del banco origen.
 *   - Cualquier otro movimiento → cargo (compra), gasto en la TDC.
 *   - Si hay 2 montos en la fila, el primero es M.N. (MXN) y es el
 *     movimiento real. El segundo es la divisa extranjera (USD), ignorado.
 */
import type { BulkParseResult, BulkParsedRow } from "./types";

const MES_MAP: Record<string, number> = {
  ene: 1,
  feb: 2,
  mar: 3,
  abr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  ago: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dic: 12,
};

const ROW_DATE_RE =
  /^(\d{1,2})\s+(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)\.?\s+(\d{4})\b(.*)$/i;

const PERIOD_RE =
  /Periodo\s+del\s+(\d{1,2})\s+(\w{3})\.?\s+(\d{4})\s+al\s+(\d{1,2})\s+(\w{3})\.?\s+(\d{4})/i;

const MONEY_RE = /\$\s*([\d,]+\.\d{2})/g;

export function parseKonfioPdf(text: string): BulkParseResult {
  const rows: BulkParsedRow[] = [];
  const errors: string[] = [];

  const periodMatch = text.match(PERIOD_RE);
  const periodStart = periodMatch
    ? toIsoSpanish(periodMatch[1]!, periodMatch[2]!, periodMatch[3]!)
    : undefined;
  const periodEnd = periodMatch
    ? toIsoSpanish(periodMatch[4]!, periodMatch[5]!, periodMatch[6]!)
    : undefined;

  const lines = text
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let inMovements = false;
  let current: {
    rawLine: string;
    day: string;
    month: string;
    year: string;
    rest: string[];
  } | null = null;
  // pdfjs a veces emite el monto ANTES de la fecha cuando reordena por Y.
  // Caso real (KONFIO 2024-10):
  //   [120] $666.10
  //   [121] 24 sep 2024
  //   [122] CMA 831005SG1 - 26 sep 2024
  //   [123] NAME-CHEAP.COM* UNBYIJ
  // Buffer la última línea-solo-de-monto vista; si la siguiente fila DD
  // mes YYYY viene sin monto, la inyectamos.
  let pendingAmountLine: string | null = null;
  const looksLikeMoneyOnlyLine = (l: string) =>
    /^\$?\s*[\d,]+\.\d{2}(\s+\$?\s*[\d,]+\.\d{2})?\s*(USD)?\s*$/.test(l);

  const flush = () => {
    if (!current) return;
    const row = buildRow(current);
    if (row.row) rows.push(row.row);
    else if (row.error) errors.push(row.error);
    current = null;
  };

  for (const line of lines) {
    // Empezamos a parsear cuando vemos la cabecera de movimientos. El PDF
    // tiene "del titular" y otra de "adicional" (servando, etc.) — todas
    // forman parte del mismo estado.
    if (/Historial de movimientos/i.test(line)) {
      flush();
      inMovements = true;
      pendingAmountLine = null;
      continue;
    }
    if (!inMovements) continue;

    // Líneas-resumen que cierran la tabla.
    if (/^Subtotal\b/i.test(line)) {
      flush();
      pendingAmountLine = null;
      continue;
    }
    // Ruido entre filas: solo el número de tarjeta o "DIGITAL"/"TITULAR" como
    // separador de columna que pdfjs reparte en líneas distintas.
    if (/^\d{3,4}$/.test(line) || /^(DIGITAL|TITULAR)$/i.test(line)) continue;
    // Header de columnas — saltar si reaparece (cuando el PDF lo repite por página).
    if (/^Fecha\b|^Descripci[oó]n\b|^Cargo\b|^Abono\b/i.test(line)) continue;

    const m = line.match(ROW_DATE_RE);
    if (m) {
      flush();
      const rest = m[4]!.trim();
      // Si la línea principal no trae monto pero teníamos uno pendiente,
      // adelantarlo dentro de rest para que buildRow lo capture.
      const restWithPending =
        !/\$?\s*[\d,]+\.\d{2}/.test(rest) && pendingAmountLine
          ? `${rest} ${pendingAmountLine}`
          : rest;
      current = {
        rawLine: line,
        day: m[1]!,
        month: m[2]!,
        year: m[3]!,
        rest: [restWithPending],
      };
      pendingAmountLine = null;
    } else if (looksLikeMoneyOnlyLine(line) && current) {
      // Línea suelta con solo un monto durante una fila activa. Si la
      // fila actual ya tiene su monto, este pertenece a la SIGUIENTE
      // fila (pdfjs reordena por Y y a veces emite el monto antes de
      // la línea de fecha). Si la actual NO tiene monto aún, le toca.
      const curHasAmount = current.rest.some((s) => /\$?\s*[\d,]+\.\d{2}/.test(s));
      if (curHasAmount) {
        pendingAmountLine = line.trim();
      } else {
        current.rest.push(line);
      }
    } else if (current) {
      current.rest.push(line);
    } else if (looksLikeMoneyOnlyLine(line)) {
      pendingAmountLine = line.trim();
    }
  }
  flush();

  return { bank: "konfio", rows, errors, periodStart, periodEnd };
}

function buildRow(cur: {
  rawLine: string;
  day: string;
  month: string;
  year: string;
  rest: string[];
}): { row?: BulkParsedRow; error?: string } {
  const date = toIsoSpanish(cur.day, cur.month, cur.year);
  const allText = cur.rest.join(" ").replace(/\s+/g, " ").trim();

  // Extraer todos los montos $X.XX en orden.
  const amounts: number[] = [];
  let mm;
  MONEY_RE.lastIndex = 0;
  while ((mm = MONEY_RE.exec(allText)) !== null) {
    amounts.push(parsePesos(mm[1]!));
  }
  if (amounts.length === 0) {
    return { error: `No pude parsear (sin monto): ${cur.day} ${cur.month} ${cur.year} ${allText}` };
  }

  // Descripción = texto sin montos, sin USD suelto.
  const description = allText
    .replace(MONEY_RE, "")
    .replace(/\s+USD\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  const isPago = /PAGO\s+(VIA\s+)?SPEI|PAGO\s+TARJETA/i.test(description);

  if (isPago) {
    const amount = amounts[0]!;
    return {
      row: {
        date,
        description: `Pago a TDC — ${description}`.slice(0, 240),
        kind: "ingreso",
        amountCents: amount,
        ivaCents: 0,
        internalTransferHint: {
          kind: "transfer_in",
          matchKey: `${date}-${amount}`,
        },
      },
    };
  }

  // Cargo (compra). Si hay 2 montos, el primero es M.N. (pesos) — el real.
  // El segundo (si existe) es la divisa extranjera y se ignora.
  return {
    row: {
      date,
      description: description.slice(0, 240),
      kind: "gasto",
      amountCents: amounts[0]!,
      ivaCents: 0,
      rawCounterparty: description.split(/\s+/).slice(0, 4).join(" "),
    },
  };
}

function toIsoSpanish(d: string, mesRaw: string, y: string): string {
  const m = MES_MAP[mesRaw.slice(0, 3).toLowerCase()];
  if (!m) return `${y}-01-01`;
  return `${y}-${String(m).padStart(2, "0")}-${d.padStart(2, "0")}`;
}

function parsePesos(s: string): number {
  if (!s) return 0;
  const n = parseFloat(s.replace(/[$,\s]/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}
